import React, { createContext, useContext, ReactNode } from 'react';

// Type for the breakdown data that each AIReportingWrapper provides
export interface AIReportingBreakdown {
  name: string;
  [key: string]: any; // Allow additional properties like emailId, emailListId, etc.
}

// Type for the tree node structure
export interface AIReportingTreeNode {
  componentName: string;
  [key: string]: any; // Additional properties from breakdown
  children?: AIReportingTreeNode[];
}

// Context for collecting AI reporting data
interface AIReportingContextType {
  registerComponent: (breakdown: AIReportingBreakdown, id?: string) => string;
  unregisterComponent: (id: string) => void;
  addChild: (parentId: string, childId: string) => void;
  getTree: () => AIReportingTreeNode[];
}

const AIReportingContext = createContext<AIReportingContextType | null>(null);

// Provider component that manages the tree structure
export const AIReportingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const componentsRef = React.useRef<Map<string, AIReportingBreakdown>>(
    new Map(),
  );
  const parentChildMapRef = React.useRef<Map<string, Set<string>>>(new Map());
  const childParentMapRef = React.useRef<Map<string, string>>(new Map());

  const registerComponent = (
    breakdown: AIReportingBreakdown,
    id?: string,
  ): string => {
    const componentId = id || Math.random().toString(36).substr(2, 9);
    componentsRef.current.set(componentId, breakdown);
    return componentId;
  };

  const unregisterComponent = (id: string) => {
    componentsRef.current.delete(id);

    // Clean up parent-child relationships
    const parent = childParentMapRef.current.get(id);
    if (parent) {
      parentChildMapRef.current.get(parent)?.delete(id);
      childParentMapRef.current.delete(id);
    }

    // Clean up children
    const children = parentChildMapRef.current.get(id);
    if (children) {
      children.forEach((childId) => {
        childParentMapRef.current.delete(childId);
      });
      parentChildMapRef.current.delete(id);
    }
  };

  const addChild = (parentId: string, childId: string) => {
    if (!parentChildMapRef.current.has(parentId)) {
      parentChildMapRef.current.set(parentId, new Set());
    }
    parentChildMapRef.current.get(parentId)!.add(childId);
    childParentMapRef.current.set(childId, parentId);
  };

  const buildTreeNode = (id: string): AIReportingTreeNode => {
    const breakdown = componentsRef.current.get(id);
    if (!breakdown) {
      throw new Error(`Component with id ${id} not found`);
    }

    const { name, ...otherProps } = breakdown;
    const node: AIReportingTreeNode = {
      componentName: name,
      ...otherProps,
    };

    const children = parentChildMapRef.current.get(id);
    if (children && children.size > 0) {
      node.children = Array.from(children).map((childId) =>
        buildTreeNode(childId),
      );
    }

    return node;
  };

  const getTree = (): AIReportingTreeNode[] => {
    // Find root components (components with no parent)
    const rootIds: string[] = [];
    Array.from(componentsRef.current.keys()).forEach((id) => {
      if (!childParentMapRef.current.has(id)) {
        rootIds.push(id);
      }
    });

    return rootIds.map((id) => buildTreeNode(id));
  };

  return (
    <AIReportingContext.Provider
      value={{
        registerComponent,
        unregisterComponent,
        addChild,
        getTree,
      }}
    >
      {children}
    </AIReportingContext.Provider>
  );
};

// Hook to access the AI reporting context
export const useAIReporting = () => {
  const context = useContext(AIReportingContext);
  if (!context) {
    throw new Error(
      'useAIReporting must be used within an AIReportingProvider',
    );
  }
  return context;
};

// Props for the AIReportingWrapper component
interface AIReportingWrapperProps {
  breakdown: AIReportingBreakdown;
  children: ReactNode;
}

// The wrapper component that registers itself and its children
export const AIReportingWrapper: React.FC<AIReportingWrapperProps> = ({
  breakdown,
  children,
}) => {
  const context = useContext(AIReportingContext);
  const [componentId] = React.useState(() =>
    Math.random().toString(36).substr(2, 9),
  );
  const parentContextRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!context) return;

    // Register this component with the pre-generated ID
    context.registerComponent(breakdown, componentId);

    return () => {
      // Unregister when unmounting
      context.unregisterComponent(componentId);
    };
  }, [context, breakdown, componentId]);

  // Create a nested context that tracks this component as the parent
  const nestedContextValue = React.useMemo(() => {
    if (!context) return null;

    return {
      ...context,
      registerComponent: (
        childBreakdown: AIReportingBreakdown,
        childId?: string,
      ): string => {
        const actualChildId = context.registerComponent(
          childBreakdown,
          childId,
        );
        context.addChild(componentId, actualChildId);
        return actualChildId;
      },
    };
  }, [context, componentId]);

  if (!context) {
    // If no context, just render children without reporting functionality
    return <>{children}</>;
  }

  return (
    <AIReportingContext.Provider value={nestedContextValue}>
      {children}
    </AIReportingContext.Provider>
  );
};

export default AIReportingWrapper;
